import React, { useEffect } from 'react';

import { connect } from 'react-redux';
import { withRouter, RouteComponentProps, useParams, useLocation } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { loadOrg } from '../../organization/actions/load-org';
import { userSelector } from '../../reducers/current-user-selectors';
import { lastVisitedOrgIdSelector } from '../../reducers/display-settings-selectors';
import { orgSelector, orgStatusSelector } from '../../reducers/org-selectors';
import { accountsByIdStatusSelector, accountsSelector } from '../account-selectors';
import { loadAccounts } from '../actions/load-accounts';

import {
  accountBilling,
  accountCarrierRegistration,
  accountOrganizations,
  accountProfile,
  accountUser,
  HOME_ROUTE,
} from '../../web-routes';

import { LoadingMessage } from '../../widgets/loading-message';

const mapStateToProps = (state: any) => {
  const user = userSelector(state);

  // Most users coming into the account page will have these filled in
  const lastVisitedOrgId = user?.username ? lastVisitedOrgIdSelector(state, user.username) : null;
  const org = lastVisitedOrgId ? orgSelector(state, lastVisitedOrgId) : null;
  const orgState = lastVisitedOrgId ? orgStatusSelector(state, lastVisitedOrgId) : null;

  // However if they don't, we need to look in the accounts they have access to and pick the first one
  // because we don't know where they are coming from
  const accounts = accountsSelector(state);
  const accountState = accountsByIdStatusSelector(state);

  return {
    user,
    accounts,
    accountState,
    org,
    orgState,
    lastVisitedOrgId,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    loadAccounts: () => dispatch(loadAccounts()),
    loadOrg: (orgId: number) => dispatch(loadOrg(orgId)),
  },
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = ReduxProps & DispatchProps & RouteComponentProps;

export const AccountLandingRedirect = withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )((props: Props) => {
    const { actions, history, accounts, accountState, org, orgState, lastVisitedOrgId } = props;

    const { accountPage } = useParams<{ accountPage: string }>();

    const { state: locationState } = useLocation();

    useEffect(() => {
      if (lastVisitedOrgId) {
        actions.loadOrg(lastVisitedOrgId);
      }
      actions.loadAccounts();
    });

    if (orgState?.loading || accountState?.loading || !accounts || !accounts.length) {
      return <LoadingMessage />;
    }

    const lastVisitedAccountId = org?.accountId || accounts[0].accountId;

    let routeFunction: (accountId?: number) => string = accountProfile;
    if (accountPage) {
      switch (accountPage) {
        case 'billing':
          routeFunction = accountBilling;
          break;
        case 'user':
          routeFunction = accountUser;
          break;
        case 'organizations':
          routeFunction = accountOrganizations;
          break;
        case 'carrier-registration':
          routeFunction = accountCarrierRegistration;
          break;
        default:
          break;
      }
    }

    if (lastVisitedAccountId) {
      history.replace(routeFunction(lastVisitedAccountId), locationState);
    } else {
      history.replace(HOME_ROUTE);
    }

    // Line 66 makes it impossible to get here
    return null;
  }),
);
